// frontend/src/components/RatingForm.js
import React, { useState } from 'react';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Checkbox } from '../components/ui/checkbox';
import CustomStarRating from './CustomStarRating';
import axios from 'axios';
import { toast } from 'sonner';

const RatingForm = ({ orderId, onSuccess }) => {
  const [rating, setRating] = useState(0);
  const [review, setReview] = useState('');
  const [isAnonymous, setIsAnonymous] = useState(false);
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async () => {
    if (rating === 0) {
      setError('Please select a star rating.');
      return;
    }

    setSubmitting(true);
    setError('');

    try {
      await axios.post(
        `${process.env.REACT_APP_API_URL}/api/order/${orderId}/rate/`,
        { 
          rating, 
          review, 
          is_anonymous: isAnonymous 
        },
        { withCredentials: true }
      );

      toast.success("Thank you for your feedback!");
      onSuccess?.();
      
      // Reset form
      setRating(0);
      setReview('');
      setIsAnonymous(false);
      setError('');
    } catch (err) {
      console.error('Rating submission failed:', err.response?.data || err.message);
      const errorMsg = err.response?.data?.error || 'Failed to submit rating. Please try again.';
      setError(errorMsg);
      toast.error(errorMsg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Card className="border-border">
      <CardHeader>
        <CardTitle className="text-xl font-semibold">Rate This Service</CardTitle>
        <p className="text-sm text-muted-foreground">
          Your feedback helps providers improve and helps other clients.
        </p>
      </CardHeader>

      <CardContent className="space-y-6">
        {/* Star Rating */}
        <div>
          <Label className="text-sm font-medium mb-3 block">How would you rate this service?</Label>
          <CustomStarRating
            count={5}
            value={rating}
            onChange={(newRating) => {
              setRating(newRating);
              setError('');
            }}
            size={28}
          />
          {rating === 0 && (
            <p className="text-xs text-muted-foreground mt-2">Click the stars to rate (1–5)</p>
          )}
        </div>

        {/* Review Textarea */}
        <div>
          <Label htmlFor="review" className="text-sm font-medium mb-2 block">
            Leave a review
          </Label>
          <Textarea
            id="review"
            value={review}
            onChange={(e) => setReview(e.target.value)}
            placeholder="What did you like? Any suggestions?"
            className="min-h-[100px] resize-y"
          />
        </div>

        {/* Anonymous Checkbox */}
        <div className="flex items-center space-x-3">
          <Checkbox
            id="anonymous"
            checked={isAnonymous}
            onCheckedChange={setIsAnonymous}
          />
          <Label htmlFor="anonymous" className="text-sm cursor-pointer">
            Post this review anonymously
          </Label>
        </div>

        {error && <p className="text-sm text-destructive">{error}</p>}

        {/* Submit Button */}
        <Button
          onClick={handleSubmit}
          disabled={submitting || rating === 0}
          className="w-full h-12 text-base font-medium"
        >
          {submitting ? 'Submitting...' : 'Submit Rating'}
        </Button>
      </CardContent>
    </Card>
  );
};

export default RatingForm;
